import { XeroContactPanel } from "@/components/XeroContactPanel";
import { Link } from "react-router-dom";
import { SyncStatusPill } from "@/components/clientmaster/SyncStatusPill";
import { RelatedClients } from "@/components/clientmaster/RelatedClients";
import { DuplicateWarning } from "@/components/clientmaster/DuplicateWarning";
import React, { useEffect, useRef, useState } from "react";
import { ClientSignedDocs } from "@/components/onboarding/ClientSignedDocs";
import { LegacyEngagement } from "@/components/onboarding/LegacyEngagement";
import { api, API, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ColumnPicker from "@/components/ColumnPicker";
import { Plus, UploadCloud, Download, Search, Sparkles, RefreshCcw, CheckCircle2, Trash2, ShieldCheck } from "lucide-react";

const ACCOUNT_MANAGERS = ["Internal", "Partners(IMNS)", "Partners(Ash)"];
const BUSINESS_TYPES = ["Pvt Ltd", "LLP", "Self Assessment", "Other"];
const ENGAGEMENT_TYPES = ["Accounting", "Consulting"];
const FINANCIAL_STATUSES = ["On-Track", "In Arrears-Minor", "In Arrears-Major", "Defaulter"];
const FIN_PILL_CLASS = {
  "On-Track": "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]",
  "In Arrears-Minor": "border-[var(--warn)] text-[var(--warn)] bg-[var(--gold-soft)]",
  "In Arrears-Major": "border-[#C2410C] text-[#C2410C] bg-[#FFEDD5]",
  Defaulter: "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]",
};
const STATUSES = ["Active", "Inactive", "Dissolved", "Prospect", "Off-Boarding"];
const SERVICES = [
  "Accounts", "Bookkeeping", "CT600 Return", "Payroll", "Confirmation Statement",
  "Self-Assessment", "Self-Assessment (Director)", "VAT", "Company Formation",
];

const STATUS_PILL_CLASS = {
  Active: "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]",
  Inactive: "border-[var(--line)] text-[var(--ink-2)]",
  Dissolved: "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]",
  Prospect: "border-[var(--navy)] text-[var(--navy)] bg-[var(--navy-soft)]",
  "Off-Boarding": "border-[var(--warn)] text-[var(--warn)] bg-[var(--gold-soft)]",
};

// Every client model field (except `notes`) is exposed to the column picker.
const CLIENT_COLUMNS = [
  { key: "business_name", label: "Business Name" },
  { key: "business_type", label: "Business Type" },
  { key: "engagement_type", label: "Engagement Type" },
  { key: "status", label: "Status" },
  { key: "financial_status", label: "Financial Status" },
  { key: "account_manager", label: "Account Manager" },
  { key: "fbs_consultant", label: "FBS Consultant" },
  { key: "registered_no", label: "Registered No." },
  { key: "services", label: "Services" },
  { key: "other_services", label: "Other Services" },
  { key: "contact_person", label: "Director / Contact" },
  { key: "director_dob", label: "Director D.O.B" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Telephone" },
  { key: "incorporation_date", label: "Incorporation Date" },
  { key: "engagement_date", label: "Date of Engagement" },
  { key: "registered_address", label: "Registered Address" },
  { key: "correspondence_address", label: "Correspondence Address" },
  { key: "xero_sync_status", label: "Xero Sync Status" },
];

// Sensible default: keep the same compact set the table used before the expansion.
const DEFAULT_VISIBLE_COLS = new Set([
  "business_type", "engagement_type", "status", "financial_status", "account_manager", "registered_no",
  "services", "contact_person", "email", "phone",
]);

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE_COLS);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: "", business_type: "", account_manager: "", engagement_type: "", financial_status: "" });
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [active, setActive] = useState(null);
  const [uploadMsg, setUploadMsg] = useState("");
  const [showImport, setShowImport] = useState(false);
  const fileRef = useRef(null);

  async function load() {
    const params = {};
    if (q) params.q = q;
    if (filters.status) params.status = filters.status;
    if (filters.business_type) params.business_type = filters.business_type;
    if (filters.engagement_type) params.engagement_type = filters.engagement_type;
    if (filters.financial_status) params.financial_status = filters.financial_status;
    if (filters.account_manager) params.account_manager = filters.account_manager;
    if (includeArchived) params.include_archived = true;
    const r = await api.get("/clients", { params });
    setItems(r.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, filters.status, filters.business_type, filters.account_manager, filters.engagement_type, filters.financial_status, includeArchived]);

  async function upload(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setUploadMsg("Uploading…");
    try {
      const r = await api.post("/clients/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadMsg(`Imported ${r.data.inserted} clients.`);
      setShowImport(false);
      load();
    } catch (err) {
      setUploadMsg(`Upload failed: ${formatErr(err.response?.data?.detail) || err.message}`);
    }
    e.target.value = "";
  }

  function exportXlsx() {
    window.open(`${API}/clients-export`, "_blank");
  }

  const anyFilter = q || filters.status || filters.business_type || filters.account_manager || filters.engagement_type || filters.financial_status;

  return (
    <div className="p-8 md:p-10">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">{items.length} clients{anyFilter ? " (filtered)" : ""}</div>
          <h1 className="font-display text-4xl">Clients.</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <ColumnPicker columns={CLIENT_COLUMNS} storageKey="finchley.clients.cols.v2" defaultVisible={Array.from(DEFAULT_VISIBLE_COLS)} onChange={setVisibleCols} testid="client-cols" />
          {isAdmin && (
            <>
              <input ref={fileRef} onChange={upload} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="client-upload-input" />
              <button data-testid="client-upload-btn" onClick={() => setShowImport(true)} className="btn-ghost flex items-center gap-2"><UploadCloud size={14} strokeWidth={1.5} /> Bulk import</button>
              <button data-testid="client-export-btn" onClick={exportXlsx} className="btn-ghost flex items-center gap-2"><Download size={14} strokeWidth={1.5} /> Bulk export</button>
              <Link to="/clients/master" data-testid="client-data-cleansing-btn" className="btn-ghost flex items-center gap-2"><ShieldCheck size={14} strokeWidth={1.5} /> Data cleansing</Link>
            </>
          )}
          <button data-testid="new-client-btn" onClick={() => setOpenNew(true)} className="btn-primary flex items-center gap-2"><Plus size={14} strokeWidth={1.5} /> New client</button>
        </div>
      </div>

      {uploadMsg && <div className="card-flat px-4 py-2 mb-4 text-sm" data-testid="upload-status">{uploadMsg}</div>}

      {/* Slim filter bar */}
      <div className="flex gap-2 flex-wrap items-center mb-5" data-testid="client-filter-bar">
        <div className="relative">
          <Search size={13} strokeWidth={1.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="input-slim pl-8 w-56" />
        </div>
        <select data-testid="filter-business-type" className="input-slim" value={filters.business_type} onChange={(e) => setFilters({ ...filters, business_type: e.target.value })}>
          <option value="">Business Type · All</option>
          {BUSINESS_TYPES.map(bt => <option key={bt} value={bt}>{bt}</option>)}
        </select>
        <select data-testid="filter-engagement-type" className="input-slim" value={filters.engagement_type} onChange={(e) => setFilters({ ...filters, engagement_type: e.target.value })}>
          <option value="">Engagement · All</option>
          {ENGAGEMENT_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="filter-client-status" className="input-slim" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">Status · All</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-financial-status" className="input-slim" value={filters.financial_status} onChange={(e) => setFilters({ ...filters, financial_status: e.target.value })}>
          <option value="">Financial Status · All</option>
          {FINANCIAL_STATUSES.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="filter-account-manager" className="input-slim" value={filters.account_manager} onChange={(e) => setFilters({ ...filters, account_manager: e.target.value })}>
          <option value="">Account Manager · All</option>
          {ACCOUNT_MANAGERS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {isAdmin && (
          <label className="flex items-center gap-1.5 cursor-pointer select-none px-2.5 py-1.5 rounded border border-[var(--line)] bg-[var(--surface)] hover:border-[var(--ink)] transition" data-testid="filter-client-archived-label" title="Include archived clients">
            <input
              type="checkbox"
              data-testid="filter-client-include-archived"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="accent-[var(--navy)] w-3.5 h-3.5"
            />
            <span className="text-xs">Show archived</span>
          </label>
        )}
        {anyFilter && (
          <button data-testid="clear-client-filters" onClick={() => { setQ(""); setFilters({ status: "", business_type: "", account_manager: "", engagement_type: "", financial_status: "" }); }} className="text-xs text-[var(--ink-2)] underline hover:text-[var(--ink)] ml-1">Clear</button>
        )}
      </div>

      {isAdmin && selected.size > 0 && (() => {
        const selectedArchivedCount = items.filter((c) => selected.has(c.id) && c.archived).length;
        return (
        <div className="card-flat p-3 mb-3 flex items-center justify-between border-l-4 border-l-[var(--gold)] flex-wrap gap-2" data-testid="client-bulk-action-bar">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> client{selected.size === 1 ? "" : "s"} selected
            {selectedArchivedCount > 0 && (
              <span className="text-[var(--ink-2)] text-xs ml-2">({selectedArchivedCount} archived)</span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="client-bulk-ch-sync-btn"
              disabled={busy}
              onClick={async () => {
                if (!window.confirm(`Sync ${selected.size} selected client${selected.size === 1 ? "" : "s"} from Companies House?\n\nThis will update deadlines and company details from CH data.`)) return;
                setBusy(true);
                try {
                  const r = await api.post("/clients/bulk-ch-sync", { client_ids: Array.from(selected) });
                  const d = r.data;
                  alert(`Sync complete.\n\n✓ ${d.ok} synced (${d.total_deadlines} deadlines upserted)\n✗ ${d.fail} failed${d.fail ? "\n\nFailures:\n" + d.results.filter((x) => !x.ok).map((x) => `• ${x.client_name || x.client_id}: ${x.error}`).join("\n") : ""}`);
                } catch (e) {
                  alert(formatErr(e.response?.data?.detail) || e.message);
                }
                setBusy(false);
                setSelected(new Set());
                load();
              }}
              className="btn-ghost text-xs flex items-center gap-1.5 !py-1.5 !px-3"
              title="Sync selected clients from Companies House"
            >
              <RefreshCcw size={11} strokeWidth={1.5} /> Sync from CH
            </button>
            <button
              data-testid="client-bulk-archive-btn"
              disabled={busy}
              onClick={async () => {
                if (!window.confirm(`Archive ${selected.size} selected client${selected.size === 1 ? "" : "s"}?`)) return;
                setBusy(true);
                try {
                  await api.patch("/clients/bulk-archive", { client_ids: Array.from(selected) });
                } catch (e) {
                  alert(formatErr(e.response?.data?.detail) || e.message);
                }
                setBusy(false);
                setSelected(new Set());
                load();
              }}
              className="btn-primary text-xs flex items-center gap-1.5 !py-1.5 !px-3"
            >
              {busy ? "Archiving…" : `Archive ${selected.size}`}
            </button>
            <button
              data-testid="client-bulk-unarchive-btn"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.patch("/clients/bulk-unarchive", { client_ids: Array.from(selected) });
                } catch (e) {
                  alert(formatErr(e.response?.data?.detail) || e.message);
                }
                setBusy(false);
                setSelected(new Set());
                load();
              }}
              className="btn-ghost text-xs !py-1.5 !px-3"
            >
              Unarchive
            </button>
            {selectedArchivedCount > 0 && (
              <button
                data-testid="client-bulk-delete-btn"
                disabled={busy}
                onClick={async () => {
                  const confirm1 = window.prompt(`Permanently delete ${selectedArchivedCount} archived client${selectedArchivedCount === 1 ? "" : "s"}?\n\nThis will ALSO delete all linked tasks and compliance deadlines. This action cannot be undone.\n\nType DELETE to confirm.`);
                  if (confirm1 !== "DELETE") return;
                  setBusy(true);
                  try {
                    const r = await api.patch("/clients/bulk-delete", { client_ids: Array.from(selected) });
                    const d = r.data;
                    alert(`Delete complete.\n\n✓ ${d.deleted} client${d.deleted === 1 ? "" : "s"} deleted\n• ${d.cascade_tasks} task${d.cascade_tasks === 1 ? "" : "s"} + ${d.cascade_compliance} deadline${d.cascade_compliance === 1 ? "" : "s"} removed\n${d.skipped ? `\n${d.skipped} skipped (not archived)` : ""}`);
                  } catch (e) {
                    alert(formatErr(e.response?.data?.detail) || e.message);
                  }
                  setBusy(false);
                  setSelected(new Set());
                  load();
                }}
                className="text-xs flex items-center gap-1.5 !py-1.5 !px-3 rounded border border-[var(--err)] text-[var(--err)] hover:bg-[var(--err-bg)] transition"
                title="Permanently delete selected ARCHIVED clients (cascades tasks + deadlines)"
              >
                <Trash2 size={11} strokeWidth={1.5} /> Delete {selectedArchivedCount} permanently
              </button>
            )}
            <button
              data-testid="client-bulk-clear-selection"
              onClick={() => setSelected(new Set())}
              className="text-xs text-[var(--ink-2)] underline hover:text-[var(--ink)]"
            >
              Clear
            </button>
          </div>
        </div>
        );
      })()}

      <div className="card-flat overflow-hidden overflow-x-auto">
        <table className="tbl min-w-[1100px]">
          <thead><tr>
            {isAdmin && (
              <th className="w-8">
                <input
                  type="checkbox"
                  data-testid="select-all-clients"
                  checked={items.length > 0 && items.every((c) => selected.has(c.id))}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) items.forEach((c) => next.add(c.id));
                    else items.forEach((c) => next.delete(c.id));
                    setSelected(next);
                  }}
                  className="accent-[var(--navy)] w-3.5 h-3.5 cursor-pointer"
                />
              </th>
            )}
            <th>Client ID</th>
            <th>Client</th>
            {visibleCols.has("business_name") && <th>Business Name</th>}
            {visibleCols.has("business_type") && <th>Business Type</th>}
            {visibleCols.has("engagement_type") && <th>Engagement Type</th>}
            {visibleCols.has("status") && <th>Status</th>}
            {visibleCols.has("financial_status") && <th>Financial Status</th>}
            {visibleCols.has("account_manager") && <th>Account Mgr</th>}
            {visibleCols.has("fbs_consultant") && <th>FBS Consultant</th>}
            {visibleCols.has("registered_no") && <th>Reg. No.</th>}
            {visibleCols.has("services") && <th>Services</th>}
            {visibleCols.has("other_services") && <th>Other Services</th>}
            {visibleCols.has("contact_person") && <th>Director / Contact</th>}
            {visibleCols.has("director_dob") && <th>Director D.O.B</th>}
            {visibleCols.has("email") && <th>Email</th>}
            {visibleCols.has("phone") && <th>Telephone</th>}
            {visibleCols.has("incorporation_date") && <th>Incorporation Date</th>}
            {visibleCols.has("engagement_date") && <th>Date of Engagement</th>}
            {visibleCols.has("registered_address") && <th>Registered Address</th>}
            {visibleCols.has("correspondence_address") && <th>Correspondence Address</th>}
            {visibleCols.has("xero_sync_status") && <th>Xero Sync Status</th>}
          </tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id} onClick={() => setActive(c)} data-testid={`client-row-${c.id}`} className={`${c.archived ? "opacity-60" : ""} ${selected.has(c.id) ? "!bg-[var(--gold-soft)]" : ""}`}>
                {isAdmin && (
                  <td onClick={(e) => e.stopPropagation()} className="!py-0">
                    <input
                      type="checkbox"
                      data-testid={`select-client-${c.id}`}
                      checked={selected.has(c.id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        e.target.checked ? next.add(c.id) : next.delete(c.id);
                        setSelected(next);
                      }}
                      className="accent-[var(--navy)] w-3.5 h-3.5 cursor-pointer"
                    />
                  </td>
                )}
                <td className="font-mono-data text-xs whitespace-nowrap" data-testid={`client-uid-${c.id}`}>{c.client_uid || "—"}</td>
                <td className="font-medium">
                  {c.archived && <span className="status-pill mr-2 border-[var(--ink-2)] text-[var(--ink-2)] bg-[var(--bg)]" title="Archived">ARCHIVED</span>}
                  {c.client_name}
                </td>
                {visibleCols.has("business_name") && <td className="text-[var(--ink-2)]">{c.business_name || "—"}</td>}
                {visibleCols.has("business_type") && <td className="text-[var(--ink-2)]">{c.business_type || "—"}</td>}
                {visibleCols.has("engagement_type") && <td className="text-[var(--ink-2)]" data-testid={`client-engagement-${c.id}`}>{c.engagement_type || "—"}</td>}
                {visibleCols.has("status") && <td>{c.status ? <span className={`status-pill ${STATUS_PILL_CLASS[c.status] || "border-[var(--line)] text-[var(--ink-2)]"}`}>{c.status}</span> : "—"}</td>}
                {visibleCols.has("financial_status") && <td data-testid={`client-finstatus-${c.id}`}>{c.financial_status ? <span className={`status-pill whitespace-nowrap ${FIN_PILL_CLASS[c.financial_status]}`}>{c.financial_status}</span> : "—"}</td>}
                {visibleCols.has("account_manager") && <td className="font-mono-data text-xs">{c.account_manager || "—"}</td>}
                {visibleCols.has("fbs_consultant") && <td className="text-xs text-[var(--ink-2)]">{c.fbs_consultant || "—"}</td>}
                {visibleCols.has("registered_no") && <td className="font-mono-data text-xs">{c.registered_no || "—"}</td>}
                {visibleCols.has("services") && <td className="text-xs text-[var(--ink-2)] max-w-[240px] truncate">{(c.services || []).join(", ") || c.service_type || "—"}</td>}
                {visibleCols.has("other_services") && <td className="text-xs text-[var(--ink-2)] max-w-[200px] truncate">{c.other_services || "—"}</td>}
                {visibleCols.has("contact_person") && <td>{c.contact_person || "—"}</td>}
                {visibleCols.has("director_dob") && <td className="font-mono-data text-xs">{(c.director_dob || "").slice(0, 10) || "—"}</td>}
                {visibleCols.has("email") && <td className="text-[var(--ink-2)] text-xs">{c.email || "—"}</td>}
                {visibleCols.has("phone") && <td className="text-[var(--ink-2)] text-xs font-mono-data">{c.phone || "—"}</td>}
                {visibleCols.has("incorporation_date") && <td className="font-mono-data text-xs">{(c.incorporation_date || "").slice(0, 10) || "—"}</td>}
                {visibleCols.has("engagement_date") && <td className="font-mono-data text-xs">{(c.engagement_date || "").slice(0, 10) || "—"}</td>}
                {visibleCols.has("registered_address") && <td className="text-xs text-[var(--ink-2)] max-w-[220px] truncate">{c.registered_address || "—"}</td>}
                {visibleCols.has("correspondence_address") && <td className="text-xs text-[var(--ink-2)] max-w-[220px] truncate">{c.correspondence_address || "—"}</td>}
                {visibleCols.has("xero_sync_status") && <td><SyncStatusPill status={c.xero_sync_status} testid={`client-sync-${c.id}`} /></td>}
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={2 + (isAdmin ? 1 : 0) + visibleCols.size} className="text-center text-[var(--ink-2)] py-12">No clients match.</td></tr>}
          </tbody>
        </table>
      </div>

      {openNew && <ClientModal onClose={(s) => { setOpenNew(false); if (s) load(); }} />}
      {active && <ClientModal client={active} onClose={(s) => { setActive(null); if (s) load(); }} />}
      {showImport && (
        <>
          <div className="backdrop" onClick={() => setShowImport(false)} />
          <div className="drawer" data-testid="import-modal">
            <div className="p-8">
              <div className="overline mb-2">Bulk import</div>
              <h2 className="font-display text-3xl mb-6">Import clients.</h2>
              <div className="text-sm text-[var(--ink-2)] mb-4">
                Upload a <span className="font-mono-data">.xlsx</span> or <span className="font-mono-data">.csv</span> file. Recognised columns: Client Name, Business Type, Status, Service Provided, Registered No., Account Manager, Incorporation Date, Date of Engagement, Director/Contact person, Director D.O.B, Telephone, Email, Registered Address, Correspondence Address.
              </div>
              <button data-testid="import-choose-file" onClick={() => fileRef.current?.click()} className="btn-primary flex items-center gap-2">
                <UploadCloud size={14} strokeWidth={1.5} /> Choose file
              </button>
              <button onClick={() => setShowImport(false)} className="btn-ghost ml-2">Close</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ClientModal({ client, onClose }) {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const isEdit = Boolean(client);
  const [form, setForm] = useState(() => ({
    client_name: "", business_name: "", contact_person: "", email: "", phone: "",
    account_manager: "", fbs_consultant: "", registered_no: "", business_type: "", engagement_type: "", status: "", financial_status: "",
    services: [], other_services: "", incorporation_date: "", engagement_date: "", director_dob: "",
    registered_address: "", correspondence_address: "", notes: "",
    ...(client || {}),
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [dupes, setDupes] = useState(null);

  function toggleService(s) {
    setForm(f => ({ ...f, services: (f.services || []).includes(s) ? f.services.filter(x => x !== s) : [...(f.services || []), s] }));
  }

  async function save(e, force = false) {
    e?.preventDefault();
    setErr(""); setSaving(true);
    try {
      if (!isEdit && !force) {
        const d = await api.post("/clients/check-duplicates", { client_name: form.client_name, business_name: form.business_name, registered_no: form.registered_no });
        if (d.data.length) { setDupes(d.data); setSaving(false); return; }
      }
      const body = { ...form };
      // strip mongo id / timestamps
      delete body.id; delete body.created_at; delete body.updated_at;
      if (isEdit) await api.put(`/clients/${client.id}`, body);
      else await api.post("/clients", body);
      onClose(true);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  }
  async function summary() {
    if (!client) return;
    const r = await api.get(`/ai/client-summary/${client.id}`);
    setAiSummary(r.data.summary);
  }

  return (
    <>
      <div className="backdrop" onClick={() => onClose(false)} />
      <div className="drawer !max-w-3xl" data-testid="client-modal">
        <form onSubmit={save} className="p-8">
          <div className="overline mb-2">{isEdit ? "Edit client" : "Create client"}</div>
          <h2 className="font-display text-3xl mb-6">
            {isEdit ? form.client_name : "New client."}
            {isEdit && form.archived && <span className="status-pill ml-3 border-[var(--ink-2)] text-[var(--ink-2)] bg-[var(--bg)] align-middle text-xs">ARCHIVED</span>}
          </h2>
          {isEdit && (
            <div className="text-xs text-[var(--ink-2)] -mt-4 mb-4" data-testid="cm-client-uid">
              Client ID: <span className="font-mono-data text-[var(--ink)]">{form.client_uid || "Not assigned"}</span>
              {form.merged_into && <span className="ml-3" data-testid="cm-merged-into">Merged into {form.merged_into.client_name}{form.merged_into.client_uid ? ` (${form.merged_into.client_uid})` : ""}</span>}
            </div>
          )}
          <div className="flex gap-2 mb-4 flex-wrap">
            {isEdit && (
              <button type="button" data-testid="ai-client-summary-btn" onClick={summary} className="btn-ghost flex items-center gap-2"><Sparkles size={14} strokeWidth={1.5} /> AI activity summary</button>
            )}
            {isEdit && isAdmin && (
              form.archived ? (
                <button
                  type="button"
                  data-testid="cm-unarchive-btn"
                  onClick={async () => {
                    try {
                      await api.patch(`/clients/${client.id}/unarchive`);
                      onClose(true);
                    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
                  }}
                  className="btn-ghost"
                >
                  Unarchive client
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="cm-archive-btn"
                  onClick={async () => {
                    if (!window.confirm("Archive this client? It will be hidden from default views. You can restore it via 'Show archived'.")) return;
                    try {
                      await api.patch(`/clients/${client.id}/archive`);
                      onClose(true);
                    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
                  }}
                  className="btn-ghost"
                >
                  Archive client
                </button>
              )
            )}
          </div>
          {isEdit && <XeroContactPanel clientId={client.id} client={client} canEdit={me?.role === "admin" || me?.role === "manager"} />}
          {isEdit && <RelatedClients clientId={client.id} canEdit={me?.role === "admin" || me?.role === "manager"} />}
          {isEdit && (me?.role === "admin" || me?.onboarding_enabled === true) && <LegacyEngagement clientId={client.id} />}
          {isEdit && (me?.role === "admin" || me?.onboarding_enabled === true) && <ClientSignedDocs clientId={client.id} ddStatus={client.dd_status} />}
          {aiSummary && <div className="card-flat ai-frame p-4 text-sm whitespace-pre-wrap mb-4" data-testid="ai-client-summary">{aiSummary}</div>}
          {err && <div className="card-flat px-3 py-2 mb-4 text-sm text-[var(--err)] border-[var(--err)] bg-[var(--err-bg)]" data-testid="cm-error">{err}</div>}

          <F label="Client / Company name (required)"><input data-testid="cm-name" required className="input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></F>

          <div className="grid grid-cols-2 gap-4">
            <F label="Account Manager">
              <select data-testid="cm-am" className="input" value={form.account_manager || ""} onChange={(e) => setForm({ ...form, account_manager: e.target.value })}>
                <option value="">— Select —</option>
                {ACCOUNT_MANAGERS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </F>
            <F label="FBS Consultant"><input data-testid="cm-fbs-consultant" className="input" value={form.fbs_consultant || ""} onChange={(e) => setForm({ ...form, fbs_consultant: e.target.value })} placeholder="e.g. name of the consultant" /></F>
          </div>
          <RadioRow label="Business Type" options={BUSINESS_TYPES} value={form.business_type} onChange={(v) => setForm({ ...form, business_type: v })} testidBase="cm-btype" />
          <RadioRow label="Engagement Type" options={ENGAGEMENT_TYPES} value={form.engagement_type} onChange={(v) => setForm({ ...form, engagement_type: v })} testidBase="cm-etype" />
          <RadioRow label="Status" options={STATUSES} value={form.status} onChange={(v) => setForm({ ...form, status: v })} testidBase="cm-status" />
          <RadioRow label="Financial Status" options={FINANCIAL_STATUSES} value={form.financial_status} onChange={(v) => setForm({ ...form, financial_status: v })} testidBase="cm-finstatus" />

          <CompaniesHousePanel
            form={form}
            setForm={setForm}
            savedClientId={isEdit ? client.id : null}
            onSyncComplete={() => onClose(true)}
          />

          <div className="grid grid-cols-2 gap-4">
            <F label="Registered No."><input data-testid="cm-regno" className="input" value={form.registered_no || ""} onChange={(e) => setForm({ ...form, registered_no: e.target.value })} /></F>
            <F label="Business Name (trading)"><input data-testid="cm-business" className="input" value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></F>
            <F label="Director / Contact person"><input data-testid="cm-contact" className="input" value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></F>
            <F label="Director D.O.B"><input data-testid="cm-dob" className="input" value={form.director_dob || ""} onChange={(e) => setForm({ ...form, director_dob: e.target.value })} placeholder="YYYY-MM-DD or free text" /></F>
            <F label="Email"><input data-testid="cm-email" className="input" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Telephone"><input data-testid="cm-phone" className="input" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
            <F label="Incorporation Date"><input data-testid="cm-inc-date" type="date" className="input" value={(form.incorporation_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, incorporation_date: e.target.value })} /></F>
            <F label="Date of Engagement"><input data-testid="cm-eng-date" type="date" className="input" value={(form.engagement_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, engagement_date: e.target.value })} /></F>
          </div>

          <F label="Registered Address"><textarea data-testid="cm-reg-addr" rows={2} className="input" value={form.registered_address || ""} onChange={(e) => setForm({ ...form, registered_address: e.target.value })} /></F>
          <F label="Correspondence Address"><textarea data-testid="cm-corr-addr" rows={2} className="input" value={form.correspondence_address || ""} onChange={(e) => setForm({ ...form, correspondence_address: e.target.value })} /></F>

          {/* Billing profile — freeform Service Fee + payment method, billing frequency and Year End Date */}
          <div className="card-flat p-4 mb-4 border-l-4 border-l-[var(--gold)]">
            <div className="overline mb-3">Billing Profile · Finance & Billing</div>
            <F label="Service Fee">
              <input data-testid="cm-service-fee" className="input" value={form.service_fee || ""} onChange={(e) => setForm({ ...form, service_fee: e.target.value })} placeholder="e.g. £150/month · £1,800 annual · £75 per return" />
            </F>
            <div className="grid grid-cols-3 gap-4">
              <F label="Payment Method">
                <select data-testid="cm-payment-method" className="input" value={form.payment_method || ""} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                  <option value="">—</option>
                  <option value="Direct Debit - Not Requested">Direct Debit - Not Requested</option>
                  <option value="Direct Debit - Requested">Direct Debit - Requested</option>
                  <option value="Direct Debit - In Place">Direct Debit - In Place</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </F>
              <F label="Billing Frequency">
                <select data-testid="cm-billing-frequency" className="input" value={form.billing_frequency || ""} onChange={(e) => setForm({ ...form, billing_frequency: e.target.value })}>
                  <option value="">—</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Annual">Annual</option>
                  <option value="One-Off">One-Off</option>
                </select>
              </F>
              <F label="Year End Date">
                <input data-testid="cm-year-end-date" type="date" className="input" value={(form.year_end_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, year_end_date: e.target.value })} />
                <div className="text-[10px] text-[var(--ink-2)] mt-1">Companies: your accounting reference date. Self-Assessment: 5 April (tax year end).</div>
              </F>
            </div>
          </div>

          <F label="Services Provided">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="cm-services">
              {SERVICES.map(s => (
                <label key={s} className={`cursor-pointer card-flat px-3 py-2 text-sm flex items-center gap-2 ${(form.services || []).includes(s) ? "border-[var(--navy)] bg-[var(--navy-soft)]" : ""}`}>
                  <input type="checkbox" checked={(form.services || []).includes(s)} onChange={() => toggleService(s)} className="accent-[var(--navy)]" data-testid={`cm-svc-${s.replace(/\s|\(|\)|\//g, "_")}`} />
                  {s}
                </label>
              ))}
            </div>
          </F>
          <F label="Other Services">
            <input data-testid="cm-other-services" className="input" value={form.other_services || ""} onChange={(e) => setForm({ ...form, other_services: e.target.value })} placeholder="Any additional / bespoke services not listed above" />
          </F>

          <F label="Notes"><textarea data-testid="cm-notes" className="input" rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>

          {dupes && <DuplicateWarning matches={dupes} onCreateAnyway={() => { setDupes(null); save(null, true); }} onCancel={() => setDupes(null)} />}
          <div className="flex gap-2 mt-6">
            <button data-testid="cm-save" disabled={saving} className="btn-primary">{saving ? "Saving…" : "Save client"}</button>
            <button type="button" onClick={() => onClose(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
}

function RadioRow({ label, options, value, onChange, testidBase }) {
  return (
    <div className="mb-4">
      <label className="label-overline">{label}</label>
      <div className="flex flex-wrap gap-2" data-testid={testidBase}>
        {options.map(o => (
          <label key={o} className={`cursor-pointer card-flat px-3 py-2 text-sm flex items-center gap-2 ${value === o ? "border-[var(--navy)] bg-[var(--navy-soft)]" : ""}`}>
            <input type="radio" name={testidBase} value={o} checked={value === o} onChange={() => onChange(o)} className="accent-[var(--navy)]" data-testid={`${testidBase}-${o.replace(/\s|\(|\)|\//g, "_")}`} />
            {o}
          </label>
        ))}
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-xs text-[var(--ink-2)] underline ml-2" data-testid={`${testidBase}-clear`}>clear</button>
        )}
      </div>
    </div>
  );
}

function F({ label, children }) { return (<div className="mb-3"><label className="label-overline">{label}</label>{children}</div>); }

/**
 * CompaniesHousePanel — inline panel inside the Client modal for Ltd/LLP/PLC clients.
 *
 * - `form` is the parent form state; `setForm` merges CH data into it (preview / verify).
 * - `savedClientId` is set only when editing an already-saved client — that's when we
 *   can call the "Sync now" endpoint (needs a persisted record + registered_no).
 * - Uses "fill blanks only" policy locally when merging into the form (mirrors backend).
 */
function CompaniesHousePanel({ form, setForm, savedClientId, onSyncComplete }) {
  const CH_BUSINESS_TYPES = ["Pvt Ltd", "LLP"]; // Only these are on the CH register
  const applicable = CH_BUSINESS_TYPES.includes(form.business_type);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");

  if (!applicable) return null;

  async function verify() {
    const cn = (form.registered_no || "").trim();
    if (!cn) { setErr("Enter the Companies House number first."); return; }
    setErr(""); setBusy(true); setPreview(null);
    try {
      const r = await api.get(`/companies-house/lookup/${encodeURIComponent(cn)}`);
      setPreview(r.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  function applyPreview() {
    if (!preview) return;
    const ra = preview.registered_office_address || {};
    const flatAddr = [ra.premises, ra.address_line_1, ra.address_line_2, ra.locality, ra.region, ra.postal_code, ra.country].filter(Boolean).join(", ");
    setForm((f) => ({
      ...f,
      registered_no: preview.company_number || f.registered_no,
      business_name: preview.company_name || f.business_name,
      incorporation_date: preview.date_of_creation || f.incorporation_date,
      // Address: fill blanks only
      registered_address: (f.registered_address || "").trim() ? f.registered_address : flatAddr,
      // Status: map CH → our status
      status: mapChStatus(preview.company_status) || f.status,
    }));
    setPreview(null);
  }

  async function syncNow() {
    if (!savedClientId) return;
    setErr(""); setBusy(true);
    try {
      const r = await api.post(`/clients/${savedClientId}/ch-sync`);
      alert(`Synced successfully.\n\n${r.data.deadlines_upserted} deadline${r.data.deadlines_upserted === 1 ? "" : "s"} upserted.\nCH status: ${r.data.ch_status || "unknown"}`);
      if (onSyncComplete) onSyncComplete();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  const lastSynced = form.chs_last_synced_at ? new Date(form.chs_last_synced_at).toLocaleString("en-GB") : null;
  const chStatus = form.chs_company_status;

  return (
    <div className="card-flat p-4 mb-4 border-l-4 border-l-[var(--navy)]" data-testid="ch-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="overline">Companies House</span>
          {chStatus && (
            <span className={`status-pill text-[10px] ${chStatus === "active" ? "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" : "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]"}`}>
              CH: {chStatus}
            </span>
          )}
          {lastSynced && <span className="text-[11px] text-[var(--ink-2)] font-mono-data">Last synced: {lastSynced}</span>}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="ch-verify-btn"
            disabled={busy || !(form.registered_no || "").trim()}
            onClick={verify}
            className="btn-ghost text-xs flex items-center gap-1.5 !py-1.5 !px-3"
            title="Fetch fresh data from Companies House but don't save yet"
          >
            <RefreshCcw size={11} strokeWidth={1.5} /> {busy ? "Fetching…" : "Verify with CH"}
          </button>
          {savedClientId && (
            <button
              type="button"
              data-testid="ch-sync-btn"
              disabled={busy || !(form.registered_no || "").trim()}
              onClick={syncNow}
              className="btn-primary text-xs flex items-center gap-1.5 !py-1.5 !px-3"
              title="Fetch CH data and auto-populate compliance deadlines"
            >
              <RefreshCcw size={11} strokeWidth={1.5} /> Sync & populate deadlines
            </button>
          )}
        </div>
      </div>

      {err && (
        <div className="card-flat px-3 py-2 mb-2 text-xs text-[var(--err)] border-[var(--err)] bg-[var(--err-bg)]" data-testid="ch-panel-error">
          {err}
        </div>
      )}

      {preview && (
        <div className="border-t border-[var(--line)] pt-3 mt-1" data-testid="ch-preview">
          <div className="text-xs text-[var(--ink-2)] mb-2">
            <CheckCircle2 size={12} strokeWidth={1.5} className="inline mr-1" />
            Preview from Companies House — review below then click Apply.
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            <ChRow label="Company name" value={preview.company_name} />
            <ChRow label="Number" value={preview.company_number} mono />
            <ChRow label="Status" value={preview.company_status} pill={preview.company_status} />
            <ChRow label="Type" value={preview.type} />
            <ChRow label="Incorporated" value={preview.date_of_creation} mono />
            <ChRow label="Jurisdiction" value={preview.jurisdiction} />
            <ChRow label="Accounts due" value={preview.accounts?.next_due} mono tone={preview.accounts?.overdue ? "err" : ""} />
            <ChRow label="Accounts period end" value={preview.accounts?.next_made_up_to} mono />
            <ChRow label="Confirmation stmt due" value={preview.confirmation_statement?.next_due} mono tone={preview.confirmation_statement?.overdue ? "err" : ""} />
            <ChRow label="CS snapshot date" value={preview.confirmation_statement?.next_made_up_to} mono />
            <div className="col-span-2">
              <ChRow label="Registered address" value={[preview.registered_office_address?.premises, preview.registered_office_address?.address_line_1, preview.registered_office_address?.address_line_2, preview.registered_office_address?.locality, preview.registered_office_address?.postal_code].filter(Boolean).join(", ")} />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button type="button" data-testid="ch-apply-preview" onClick={applyPreview} className="btn-primary text-xs !py-1.5 !px-3">Apply to form</button>
            <button type="button" data-testid="ch-dismiss-preview" onClick={() => setPreview(null)} className="btn-ghost text-xs !py-1.5 !px-3">Discard</button>
          </div>
          {!savedClientId && (
            <div className="text-[11px] text-[var(--ink-2)] mt-2 italic">
              Compliance deadlines will be populated automatically after you save this client.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChRow({ label, value, mono, pill, tone }) {
  const cls = tone === "err" ? "text-[var(--err)]" : "text-[var(--ink)]";
  return (
    <div className="flex justify-between gap-2 py-0.5">
      <span className="text-[var(--ink-2)]">{label}</span>
      {pill ? (
        <span className={`status-pill text-[10px] ${pill === "active" ? "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" : "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]"}`}>{value || "—"}</span>
      ) : (
        <span className={`${mono ? "font-mono-data" : ""} ${cls} text-right truncate max-w-[220px]`} title={value || ""}>{value || "—"}</span>
      )}
    </div>
  );
}

function mapChStatus(chStatus) {
  if (!chStatus) return null;
  const s = String(chStatus).toLowerCase();
  if (s === "active") return "Active";
  if (["dissolved", "removed", "converted-closed"].includes(s)) return "Dissolved";
  if (["liquidation", "receivership", "administration", "voluntary-arrangement", "insolvency-proceedings"].includes(s)) return "Off-Boarding";
  return "Inactive";
}
